import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import {
  ArrowLeft, Mail, Megaphone, Calendar, CalendarDays, AlertTriangle, CreditCard,
  MessageCircle, Users2, Paperclip, CheckCircle2, Circle, AlertCircle,
} from 'lucide-react';

function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}
function fmtDay(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

function PageShell({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        {icon}
        <h1 className="text-xl font-semibold">{title}</h1>
      </header>
      <main className="max-w-4xl mx-auto p-4 sm:p-6">{children}</main>
    </div>
  );
}

/* ========== Elternbriefe ========== */

interface Letter {
  letter_no: number; title: string; body: string | null;
  posted_at: string | null; classes: string | null;
  has_attachment: boolean; attachment_url: string | null; acknowledged: boolean;
}

export const ElternbriefePage: React.FC = () => {
  const [items, setItems] = useState<Letter[]>([]);
  const [onlyUnack, setOnlyUnack] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ letters: Letter[] }>('/api/eltern-portal/elternbriefe').then(r => setItems(r.letters)).catch(e => setError(e?.message || 'Fehler'));
  }, []);
  const shown = useMemo(() => onlyUnack ? items.filter(i => !i.acknowledged) : items, [items, onlyUnack]);
  return (
    <PageShell title="Elternbriefe" icon={<Mail className="w-6 h-6 text-blue-400" />}>
      <label className="mb-3 flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={onlyUnack} onChange={e => setOnlyUnack(e.target.checked)} className="accent-blue-500" />
        Nur unbestätigte anzeigen
      </label>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {shown.length === 0 ? (
        <p className="text-gray-500 text-sm">Keine Elternbriefe.</p>
      ) : (
        <ul className="space-y-3">
          {shown.map(l => (
            <li key={l.letter_no} className={`bg-gray-800 border ${l.acknowledged ? 'border-gray-700' : 'border-yellow-500/50'} rounded-lg p-4`}>
              <div className="flex items-start gap-3 flex-wrap">
                <span className="font-mono text-gray-400 text-sm">#{l.letter_no}</span>
                <h3 className="font-semibold flex-1 min-w-[200px]">{l.title}</h3>
                {l.acknowledged
                  ? <span className="text-xs bg-green-500/20 text-green-300 border border-green-500/40 px-2 py-1 rounded flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> bestätigt</span>
                  : <span className="text-xs bg-yellow-500/20 text-yellow-200 border border-yellow-500/40 px-2 py-1 rounded flex items-center gap-1"><Circle className="w-3 h-3" /> nicht bestätigt</span>}
              </div>
              <div className="mt-1 text-xs text-gray-400 flex flex-wrap gap-3">
                {l.posted_at && <span>📅 {fmtDateTime(l.posted_at)}</span>}
                {l.classes && <span className="bg-gray-700 px-1.5 py-0.5 rounded">Klasse/n: {l.classes}</span>}
                {l.has_attachment && l.attachment_url && (
                  <a href={l.attachment_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> Anhang
                  </a>
                )}
              </div>
              {l.body && <p className="mt-3 text-sm text-gray-200 whitespace-pre-wrap">{l.body}</p>}
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
};

/* ========== Schwarzes Brett ========== */

interface BoardPost { title: string; body: string | null; posted_at: string | null; attachment_url: string | null; }

export const ElternBrettPage: React.FC = () => {
  const [items, setItems] = useState<BoardPost[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ posts: BoardPost[] }>('/api/eltern-portal/schwarzes-brett').then(r => setItems(r.posts)).catch(e => setError(e?.message || 'Fehler'));
  }, []);
  return (
    <PageShell title="Schwarzes Brett" icon={<Megaphone className="w-6 h-6 text-orange-400" />}>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">Keine Einträge.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((p, i) => (
            <li key={i} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <h3 className="font-semibold">{p.title}</h3>
              {p.posted_at && <p className="text-xs text-gray-400 mt-1">eingestellt am {fmtDateTime(p.posted_at)}</p>}
              {p.body && <p className="mt-2 text-sm text-gray-200 whitespace-pre-wrap">{p.body}</p>}
              {p.attachment_url && (
                <a href={p.attachment_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300">
                  <Paperclip className="w-3 h-3" /> Anhang
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
};

/* ========== Termine ========== */

interface PortalTermin { external_id: string; title: string; starts_at: string; ends_at: string | null; bo_end: number; event_class: string | null; }

function cleanTitle(raw: string): string {
  return raw.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
}

export const ElternTerminePage: React.FC = () => {
  const [items, setItems] = useState<PortalTermin[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ termine: PortalTermin[] }>('/api/eltern-portal/termine').then(r => setItems(r.termine)).catch(e => setError(e?.message || 'Fehler'));
  }, []);
  const upcoming = items.filter(e => new Date(e.starts_at).getTime() >= Date.now() - 24 * 3600 * 1000);
  return (
    <PageShell title="Termine" icon={<Calendar className="w-6 h-6 text-purple-400" />}>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {upcoming.length === 0 ? (
        <p className="text-gray-500 text-sm">Keine anstehenden Termine.</p>
      ) : (
        <ul className="space-y-2">
          {upcoming.map((e, i) => (
            <li key={i} className={`bg-gray-800 border-l-4 ${e.event_class?.includes('sa') ? 'border-red-500' : e.event_class?.includes('info') ? 'border-blue-500' : 'border-purple-500'} border-y border-r border-gray-700 rounded-lg p-4`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="bg-purple-500/20 text-purple-200 rounded-md px-3 py-2 text-center min-w-[90px]">
                  <div className="text-xs">{fmtDay(e.starts_at)}</div>
                  {e.bo_end !== 3 && <div className="text-sm font-mono">{new Date(e.starts_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium whitespace-pre-wrap">{cleanTitle(e.title)}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
};

/* ========== Stundenplan ========== */

interface TimetableEntry { hour_label: string; day_index: number; day_label: string; cell: string | null; }

// Same palette + hash as the Schüler-Portal Stundenplan, for consistent colors across both views.
const SP_COLORS = [
  'bg-blue-500/20 text-blue-200 border-blue-500/40',
  'bg-red-500/20 text-red-200 border-red-500/40',
  'bg-green-500/20 text-green-200 border-green-500/40',
  'bg-yellow-500/20 text-yellow-200 border-yellow-500/40',
  'bg-purple-500/20 text-purple-200 border-purple-500/40',
  'bg-pink-500/20 text-pink-200 border-pink-500/40',
  'bg-indigo-500/20 text-indigo-200 border-indigo-500/40',
  'bg-teal-500/20 text-teal-200 border-teal-500/40',
  'bg-orange-500/20 text-orange-200 border-orange-500/40',
  'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',
];
function colorFor(code: string): string {
  let h = 0;
  for (const c of code) h = (h * 31 + c.charCodeAt(0)) | 0;
  return SP_COLORS[Math.abs(h) % SP_COLORS.length];
}

// Eltern-Portal cell strings look like "Mu H-EG 01", "NuT_Ph B-EG 05",
// "F/L G-EG 15/G-EG 02". Split: first token is the subject code, rest the room.
function parseCell(raw: string | null): { subject: string; room: string } | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  const idx = s.search(/\s/);
  if (idx === -1) return { subject: s, room: '' };
  return { subject: s.slice(0, idx), room: s.slice(idx + 1).trim() };
}

// Extract the "1." prefix from hour labels like "1. 08.15 - 09.00"
function shortHour(label: string): { num: string; time: string } {
  const m = label.match(/^\s*(\d+\.)\s*(.*)$/);
  if (!m) return { num: label, time: '' };
  return { num: m[1], time: m[2] };
}

export const ElternStundenplanPage: React.FC = () => {
  const [items, setItems] = useState<TimetableEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ entries: TimetableEntry[] }>('/api/eltern-portal/stundenplan').then(r => setItems(r.entries)).catch(e => setError(e?.message || 'Fehler'));
  }, []);

  const grouped = useMemo(() => {
    const byHour = new Map<string, Map<number, string | null>>();
    const days = new Set<number>();
    const dayLabels: Record<number, string> = {};
    const hourOrder: string[] = [];
    for (const e of items) {
      if (!byHour.has(e.hour_label)) { byHour.set(e.hour_label, new Map()); hourOrder.push(e.hour_label); }
      byHour.get(e.hour_label)!.set(e.day_index, e.cell);
      days.add(e.day_index);
      dayLabels[e.day_index] = e.day_label;
    }
    // Sort hour labels by leading number if present
    hourOrder.sort((a, b) => {
      const na = parseInt(a.match(/^\s*(\d+)/)?.[1] ?? '0', 10);
      const nb = parseInt(b.match(/^\s*(\d+)/)?.[1] ?? '0', 10);
      return na - nb;
    });
    return {
      hours: hourOrder,
      dayIndexes: [...days].sort((a, b) => a - b),
      dayLabels,
      byHour,
    };
  }, [items]);

  // Unique subjects for the bottom legend
  const subjects = useMemo(() => {
    const s = new Set<string>();
    for (const e of items) {
      const p = parseCell(e.cell);
      if (p) s.add(p.subject);
    }
    return [...s].sort();
  }, [items]);

  // Short day labels ("Montag" → "Mo") for a more compact header
  const shortDay = (d: string) => (d || '').slice(0, 2);

  return (
    <PageShell title="Stundenplan (Eltern-Portal)" icon={<CalendarDays className="w-6 h-6 text-cyan-400" />}>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">Keine Daten. Sync auslösen, dann lädt der Stundenplan.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[640px]">
            <thead>
              <tr>
                <th className="sticky left-0 bg-gray-900 p-2 text-left text-xs text-gray-500 font-normal w-16">Std.</th>
                {grouped.dayIndexes.map(d => (
                  <th key={d} className="p-2 text-sm text-gray-300 font-medium text-center">{shortDay(grouped.dayLabels[d])}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.hours.map(h => {
                const { num, time } = shortHour(h);
                return (
                  <tr key={h} className="border-t border-gray-800">
                    <td className="sticky left-0 bg-gray-900 p-2 text-center">
                      <div className="text-sm text-gray-400">{num}</div>
                      {time && <div className="text-[10px] text-gray-600 font-mono leading-tight">{time}</div>}
                    </td>
                    {grouped.dayIndexes.map(d => {
                      const cell = grouped.byHour.get(h)?.get(d) ?? null;
                      const parsed = parseCell(cell);
                      return (
                        <td key={d} className="p-1 align-top">
                          {parsed ? (
                            <div className={`border rounded-md p-2 h-full min-h-[56px] ${colorFor(parsed.subject)}`}>
                              <div className="font-semibold text-sm">{parsed.subject}</div>
                              {parsed.room && <div className="text-xs opacity-80 mt-1">{parsed.room}</div>}
                            </div>
                          ) : (
                            <div className="border border-dashed border-gray-800 rounded-md min-h-[56px]" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {subjects.length > 0 && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold mb-3">Fächer ({subjects.length})</h2>
          <div className="flex flex-wrap gap-2">
            {subjects.map(s => (
              <span key={s} className={`text-xs px-2 py-1 rounded border ${colorFor(s)}`}>{s}</span>
            ))}
          </div>
        </section>
      )}
    </PageShell>
  );
};

/* ========== Vertretungsplan ========== */

interface Substitution { hour_label: string; affected: string; substitution: string; subject: string; room: string; info: string; }

export const ElternVertretungsPage: React.FC = () => {
  const [items, setItems] = useState<Substitution[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ substitutions: Substitution[] }>('/api/eltern-portal/vertretungsplan').then(r => setItems(r.substitutions)).catch(e => setError(e?.message || 'Fehler'));
  }, []);
  return (
    <PageShell title="Vertretungsplan" icon={<AlertTriangle className="w-6 h-6 text-yellow-400" />}>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">Keine Vertretungen.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-800 text-xs uppercase text-gray-400">
            <tr>
              <th className="text-left p-2">Std.</th>
              <th className="text-left p-2">Betrifft</th>
              <th className="text-left p-2">Vertretung</th>
              <th className="text-left p-2">Fach</th>
              <th className="text-left p-2">Raum</th>
              <th className="text-left p-2">Info</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={i} className="border-b border-gray-700/50">
                <td className="p-2 font-mono">{s.hour_label}</td>
                <td className="p-2">{s.affected}</td>
                <td className="p-2">{s.substitution}</td>
                <td className="p-2">{s.subject}</td>
                <td className="p-2">{s.room}</td>
                <td className="p-2 text-gray-400">{s.info}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PageShell>
  );
};

/* ========== Zahlungen ========== */

interface Payment { received_at: string | null; child_name: string | null; subject: string; teacher: string | null; amount_cents: number | null; amount_raw: string; status: string; }

export const ElternPaymentsPage: React.FC = () => {
  const [items, setItems] = useState<Payment[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ payments: Payment[] }>('/api/eltern-portal/payments').then(r => setItems(r.payments)).catch(e => setError(e?.message || 'Fehler'));
  }, []);
  const open = items.filter(p => !/bezahlt/i.test(p.status));
  const paid = items.filter(p => /bezahlt/i.test(p.status));

  const Section = ({ title, rows, highlight }: { title: string; rows: Payment[]; highlight: boolean }) => (
    <section className={`bg-gray-800 border rounded-lg p-4 ${highlight ? 'border-red-500/40' : 'border-gray-700'}`}>
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        {highlight && <AlertCircle className="w-4 h-4 text-red-400" />} {title}
      </h3>
      {rows.length === 0 ? <p className="text-gray-500 text-sm">Keine Einträge.</p> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-400">
            <tr>
              <th className="text-left p-2">Datum</th>
              <th className="text-left p-2">Kind</th>
              <th className="text-left p-2">Betreff</th>
              <th className="text-left p-2">Lehrkraft</th>
              <th className="text-right p-2">Betrag</th>
              <th className="text-left p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={i} className="border-b border-gray-700/50">
                <td className="p-2 whitespace-nowrap">{fmtDate(p.received_at)}</td>
                <td className="p-2">{p.child_name}</td>
                <td className="p-2">{p.subject}</td>
                <td className="p-2 text-gray-400 text-xs">{p.teacher}</td>
                <td className="p-2 text-right font-mono">{p.amount_raw}</td>
                <td className="p-2">
                  <span className={`text-xs px-2 py-0.5 rounded border ${/bezahlt/i.test(p.status) ? 'bg-green-500/20 text-green-200 border-green-500/40' : 'bg-yellow-500/20 text-yellow-200 border-yellow-500/40'}`}>
                    {p.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );

  return (
    <PageShell title="Zahlungen" icon={<CreditCard className="w-6 h-6 text-green-400" />}>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <div className="space-y-4">
        <Section title="Offene Zahlungen" rows={open} highlight={open.length > 0} />
        <Section title="Bezahlt" rows={paid} highlight={false} />
      </div>
    </PageShell>
  );
};

/* ========== Kommunikation (Thread-Liste) ========== */

interface Thread { kind: 'klassenleitung' | 'fachlehrer'; teacher_name: string | null; subject: string; last_message_at: string | null; unread: boolean; has_attachment: boolean; last_sender: string | null; }

export const ElternKommunikationPage: React.FC = () => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ threads: Thread[] }>('/api/eltern-portal/threads').then(r => setThreads(r.threads)).catch(e => setError(e?.message || 'Fehler'));
  }, []);
  const groups = useMemo(() => ({
    klasse: threads.filter(t => t.kind === 'klassenleitung'),
    fachlehrer: threads.filter(t => t.kind === 'fachlehrer'),
  }), [threads]);

  const Group = ({ title, rows }: { title: string; rows: Thread[] }) => (
    <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <h3 className="font-semibold mb-3">{title}</h3>
      {rows.length === 0 ? <p className="text-gray-500 text-sm">Keine Konversationen.</p> : (
        <ul className="divide-y divide-gray-700/60">
          {rows.map((t, i) => (
            <li key={i} className="py-2">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${t.unread ? 'font-bold text-white' : 'text-gray-200'}`}>
                    {t.teacher_name}
                    {t.has_attachment && <Paperclip className="inline w-3 h-3 ml-1 text-blue-400" />}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{t.subject}</div>
                </div>
                <div className="text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(t.last_message_at)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <PageShell title="Kommunikation" icon={<MessageCircle className="w-6 h-6 text-pink-400" />}>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <div className="space-y-4">
        <Group title="Klassenleitung" rows={groups.klasse} />
        <Group title="Fachlehrkräfte" rows={groups.fachlehrer} />
        <p className="text-xs text-gray-500">
          Nachrichten-Inhalte lassen sich hier nur listen; zum Lesen/Antworten nutze bitte weiterhin direkt das Eltern-Portal.
        </p>
      </div>
    </PageShell>
  );
};

/* ========== Lehrkräfte ========== */

interface TeacherRow { subject: string; teacher_name: string; group_label: string | null; }

export const ElternKollegiumPage: React.FC = () => {
  const [items, setItems] = useState<TeacherRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ teachers: TeacherRow[] }>('/api/eltern-portal/teachers').then(r => setItems(r.teachers)).catch(e => setError(e?.message || 'Fehler'));
  }, []);
  const grouped = useMemo(() => {
    const out = new Map<string, TeacherRow[]>();
    for (const r of items) {
      const key = r.group_label ?? '—';
      if (!out.has(key)) out.set(key, []);
      out.get(key)!.push(r);
    }
    return out;
  }, [items]);

  return (
    <PageShell title="Lehrkräfte" icon={<Users2 className="w-6 h-6 text-gray-300" />}>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">Keine Daten.</p>
      ) : (
        <div className="space-y-4">
          {[...grouped.entries()].map(([group, rows]) => (
            <section key={group} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-sm uppercase tracking-wider text-gray-400">{group}</h3>
              <ul className="divide-y divide-gray-700/50">
                {rows.map((r, i) => (
                  <li key={i} className="py-1.5 flex items-center gap-3">
                    <span className="text-gray-300 text-sm flex-1">{r.subject}</span>
                    <span className="text-white">{r.teacher_name}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </PageShell>
  );
};
